import { AnimatePresence, motion } from "motion/react";
import { useFormContext, useWatch } from "react-hook-form";
import { amortizationMethodsListRealEstate } from "utils";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";
import type { BidPostData } from "validations";

import { ControlledIntegerInput } from "@krea/shared/design-system/ControlledInputs/IntegerInput";
import { MoneyInput } from "@krea/shared/design-system/ControlledInputs/MoneyInput";
import { ControlledStringInput } from "@krea/shared/design-system/ControlledInputs/StringInput";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@krea/shared/design-system/Form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import type { Currency } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";

import { AmortizationMethodEnum } from "@/features/common/enums";
import type { IRealEstateBidInput } from "@/features/lender-responses/types/lender-response";

import DetailsItem from "../../../../details/item";

const lenderMonthlyInputVariants = {
  initial: {
    opacity: 0,
    x: "-100vw",
  },
  animate: {
    opacity: 1,
    x: 0,
  },
  exit: {
    opacity: 0,
    x: "-100vw",
    transition: {
      duration: 0.6,
    },
  },
};
const lenderMonthlyInputTransition = undefined; // { type: 'tween', duration: 0.6, ease: 'anticipate' }; // 'anticipate', 'linear'

function AmortizationMethodSelect({
  value,
  onChange,
  onBlur,
  handleAmortizationMethodChange,
  isSubmitting,
  filteredOptions,
  placeholder,
}: {
  value: AmortizationMethodEnum;
  onChange: (v: AmortizationMethodEnum) => void;
  onBlur: () => void;
  handleAmortizationMethodChange: (
    oldValue: AmortizationMethodEnum,
    newValue?: AmortizationMethodEnum,
  ) => void;
  isSubmitting: boolean;
  filteredOptions: { value: AmortizationMethodEnum; label: string }[];
  placeholder: string;
}) {
  const { formItemId } = useFormField();

  return (
    <Select
      value={String(value)}
      onValueChange={(v) => {
        handleAmortizationMethodChange(value, v as AmortizationMethodEnum);
        onChange(v as AmortizationMethodEnum);
      }}
      onOpenChange={(open) => {
        if (!open) onBlur();
      }}
      disabled={isSubmitting}
      required
    >
      <SelectTrigger id={formItemId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {filteredOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface LoanFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean; // Is editing existing offer or creating new
  isInterestRatePerYearActivated?: boolean;
}

export const RealEstateBidForm = ({
  bidFormIndex,
  currency,
  isEditing,
  isInterestRatePerYearActivated,
}: LoanFormProps) => {
  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<BidPostData<IRealEstateBidInput>>();

  const bid = useWatch({
    control,
    name: `bids.${bidFormIndex}`,
  });

  const [, calculatedCurrency] = useFormatCurrency({
    currency,
  });

  const handleAmortizationMethodChange = (
    oldValue: AmortizationMethodEnum,
    newValue?: AmortizationMethodEnum,
  ) => {
    if (
      oldValue === AmortizationMethodEnum.FIXED &&
      newValue !== AmortizationMethodEnum.FIXED
    ) {
      setValue(`bids.${bidFormIndex}.lenderMonthlyAmount`, undefined);
    }

    if (
      oldValue !== AmortizationMethodEnum.FIXED &&
      newValue === AmortizationMethodEnum.FIXED
    ) {
      setValue(`bids.${bidFormIndex}.lenderMonthlyInterest`, undefined);
    }
  };

  return (
    <div className="flex w-full flex-col">
      <div className="w-full">
        <DetailsItem
          title={isEditing ? "Update real estate terms" : "Real estate terms"}
          className="mb-0"
        >
          <div className="w-full space-y-4">
            <MoneyInput
              name={`bids.${bidFormIndex}.amount`}
              label="Amount"
              control={control}
              required
              currency={calculatedCurrency!}
            />
            <ControlledIntegerInput
              name={`bids.${bidFormIndex}.paybackTimeMonths`}
              label="Loan term"
              control={control}
              required
              rightText="months"
              acceptsDecimals={false}
            />
          </div>
        </DetailsItem>

        <DetailsItem className="mb-0">
          <div className="w-full">
            <FormField
              control={control}
              name={`bids.${bidFormIndex}.amortizationMethod`}
              render={({ field: { value, onChange, onBlur }, fieldState }) => {
                const filteredOptions =
                  amortizationMethodsListRealEstate.filter(
                    (method) =>
                      !(
                        isInterestRatePerYearActivated &&
                        method.value === AmortizationMethodEnum.FIXED
                      ),
                  );

                return (
                  <FormItem>
                    <FormLabel
                      className={cn(
                        "font-bold",
                        fieldState.invalid && "text-red-500",
                      )}
                    >
                      Amortization method{" "}
                      <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <AmortizationMethodSelect
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        handleAmortizationMethodChange={
                          handleAmortizationMethodChange
                        }
                        isSubmitting={isSubmitting}
                        filteredOptions={filteredOptions}
                        placeholder="Select amortization method"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
        </DetailsItem>

        <DetailsItem className="mb-0" title="">
          <div className="w-full">
            <MoneyInput
              name={`bids.${bidFormIndex}.totalCost`}
              label="Total cost (inc. setup fee) + approved amount"
              control={control}
              required
              currency={calculatedCurrency!}
            />
          </div>
        </DetailsItem>

        <DetailsItem title="">
          <div className="flex w-full flex-col gap-4">
            <div className="mb-2 ml-3 w-full">
              <span className="text-sm italic text-gray-600">
                {'Here you can explain what the "Total cost" consists of:'}
              </span>
            </div>
            <div className="w-full">
              <div className="mb-0 ml-3 space-y-4 border-l pl-3">
                <AnimatePresence mode="wait" initial={false}>
                  {bid.amortizationMethod === AmortizationMethodEnum.LINEAR && (
                    <ControlledIntegerInput
                      name={`bids.${bidFormIndex}.amortizationInPercent`}
                      label="Amortisation in % of loan amount"
                      control={control}
                      required
                      rightText="%"
                      placeholder="Value in %"
                    />
                  )}
                  {bid.amortizationMethod === AmortizationMethodEnum.FIXED ? (
                    <DetailsItem className="mb-0">
                      <div className="w-full">
                        <motion.div
                          key="lender_monthly_amount"
                          initial="initial"
                          exit="exit"
                          animate="animate"
                          variants={lenderMonthlyInputVariants}
                        >
                          <MoneyInput
                            name={`bids.${bidFormIndex}.lenderMonthlyAmount`}
                            label="Fixed Fee per 30 days"
                            control={control}
                            required
                            currency={calculatedCurrency!}
                            placeholder={`Value in "${calculatedCurrency}"`}
                          />
                        </motion.div>
                      </div>
                    </DetailsItem>
                  ) : (
                    <DetailsItem className="mb-0">
                      <div className="w-full">
                        <motion.div
                          key="lender_monthly_interest"
                          initial="initial"
                          exit="exit"
                          animate="animate"
                          variants={lenderMonthlyInputVariants}
                          transition={lenderMonthlyInputTransition}
                        >
                          <ControlledIntegerInput
                            name={`bids.${bidFormIndex}.lenderMonthlyInterest`}
                            label={
                              isInterestRatePerYearActivated
                                ? "Interest per year"
                                : "Interest per 30 days"
                            }
                            control={control}
                            required
                            rightText="%"
                            placeholder="Value in %"
                          />
                        </motion.div>
                      </div>
                    </DetailsItem>
                  )}
                </AnimatePresence>

                <MoneyInput
                  name={`bids.${bidFormIndex}.setupFee`}
                  label="Setup fee"
                  control={control}
                  currency={calculatedCurrency!}
                />

                <ControlledStringInput
                  name={`bids.${bidFormIndex}.earlyRepaymentFee`}
                  label="Cost for early repayment"
                  control={control}
                  className="rounded"
                  required={false}
                />

                <MoneyInput
                  name={`bids.${bidFormIndex}.postalFee`}
                  label="Postal fee per month"
                  control={control}
                  currency={calculatedCurrency!}
                />

                <MoneyInput
                  name={`bids.${bidFormIndex}.directDebitFee`}
                  label="Direct debit fee per month"
                  control={control}
                  currency={calculatedCurrency!}
                />
              </div>
            </div>
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
